import { useEffect, useRef, useState } from 'react';

import { Button } from '@mui/material';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';

import PageFooter from 'src/components/PageFooter';
import PageHeader from 'src/components/PageHeader';
import VisitScheduleForm, { type VisitScheduleFormHandle } from 'src/components/VisitScheduleForm';
import { useAnalytics } from 'src/contexts/AnalyticsContext';
import { useSnackbar } from 'src/contexts/SnackbarContext';
import { useTeamMemberData } from 'src/hooks/useTeamMemberData';
import { InnerMaxWidthContainer } from 'src/layouts/InnerMaxWidthContainer';
import { useCreateVisitScheduleMutation } from 'src/store/sageHomeApi';

export const Route = createFileRoute('/(app)/visits/add')({
    component: AddVisitComponent,
    validateSearch: (search: Record<string, unknown>) => {
        return {
            type: (search.type as 'single' | 'repeating') || 'single',
        };
    },
});

const PageName = 'Add Visit';

function AddVisitComponent() {
    const navigate = useNavigate();
    const { type } = useSearch({ from: '/(app)/visits/add' });

    const { trackPageView } = useAnalytics();
    const { showSuccess, showError } = useSnackbar();

    const { careRecipient } = useTeamMemberData();

    const formRef = useRef<VisitScheduleFormHandle>(null);
    const [isFormValid, setIsFormValid] = useState(false);

    const isRepeating = type === 'repeating';

    const [createVisitSchedule, { isLoading: isSubmitting }] = useCreateVisitScheduleMutation();

    useEffect(() => {
        trackPageView(PageName);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSubmit = async (data: {
        caregiverRid: string;
        scheduleStartInstant: number;
        visitDurationMinutes: number;
        cronExpression?: string;
        ianaTimezone?: string;
    }) => {
        if (!careRecipient?.teamMemberRid) {
            showError('Care recipient not found');
            return;
        }

        try {
            await createVisitSchedule({
                careRecipientRid: careRecipient.teamMemberRid,
                createVisitScheduleRequest: {
                    caregiverRid: data.caregiverRid,
                    scheduleStartInstant: data.scheduleStartInstant,
                    visitDurationMinutes: data.visitDurationMinutes,
                    cronExpression: data.cronExpression,
                    ianaTimezone: data.ianaTimezone || 'America/New_York',
                },
            }).unwrap();

            showSuccess(
                isRepeating ? 'Repeating visit schedule created' : 'One-time visit schedule created'
            );
            navigate({ to: '/visits' });
        } catch (error) {
            console.error('Failed to create visit:', error);
            showError('An error occurred, please try again.');
        }
    };

    const handleSubmit = () => {
        formRef.current?.submit();
    };

    return (
        <InnerMaxWidthContainer>
            <PageHeader pageTitle={isRepeating ? 'Schedule Visit' : 'Add One-Time Visit'} />

            <VisitScheduleForm
                ref={formRef}
                isRepeating={isRepeating}
                onSubmit={onSubmit}
                onValidityChange={setIsFormValid}
                showChangeCaregiverButton
            />

            <PageFooter includeShadow>
                <Button
                    variant="contained"
                    fullWidth
                    color="primary"
                    onClick={handleSubmit}
                    disabled={!isFormValid || isSubmitting}
                >
                    {isSubmitting ? 'Creating...' : isRepeating ? 'Schedule Visit' : 'Add Visit'}
                </Button>
            </PageFooter>
        </InnerMaxWidthContainer>
    );
}
